"use client";

import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import type { Locale } from "@/lib/i18n";
import styles from "./Form.module.css";
import { useFieldContext } from "./Field";

/**
 * Input — `<input>` z brand-styled stanami + floating label support.
 *
 * **Auto-binding z `<Field>`:** gdy zagnieżdżony w `<Field>`, automatycznie
 * dostaje `id`, `aria-describedby`, `aria-invalid`, `aria-required`, oraz
 * **`placeholder=" "`** (pusty space) wymagany przez floating label
 * (`:placeholder-shown` selector). Konsument może nadpisać przez własny
 * `placeholder="..."` prop.
 *
 * **Standalone (bez Field):** zwykły `<input>` + brand styling, bez floating
 * label. Placeholder działa normalnie.
 *
 * **State variants (explicit props nadpisują context):**
 *   - `invalid` -> red border + danger focus ring + label color danger
 *   - `success` -> green border + label color success
 *
 * **Slots:** `prefix`/`suffix` (icon/text obok pola w tej samej ramce).
 * Floating label działa razem z prefix/suffix — `Form.module.css` przez
 * `:has()` wykrywa wrapper `.inputWithAddon` i (a) animuje label gdy
 * control wewnątrz jest focused/wypełniony, (b) offsetuje label w prawo
 * (`--label-offset`) żeby nie nakładał się na obszar ikony.
 *
 * **Password toggle:** dla `type="password"` automatycznie renderuje przycisk
 * 👁 (eye) overlay na prawej krawędzi pola — klik przełącza visibility na
 * `text` ↔ `password`. Wyłączane przez `showPasswordToggle={false}`. Działa
 * razem z prefix/suffix (button siedzi wewnątrz input boxa, nie zastępuje
 * suffix slotu).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    invalid,
    success,
    prefix,
    suffix,
    className,
    type,
    showPasswordToggle = true,
    ...rest
  },
  ref,
) {
  const ctx = useFieldContext();
  const t = useT(passwordToggleI18n);
  const [revealed, setRevealed] = useState(false);

  const effectiveInvalid = invalid ?? ctx?.invalid ?? false;
  const stateClass = effectiveInvalid
    ? styles.invalid
    : success
      ? styles.success
      : "";

  /* Floating label wymaga `:placeholder-shown` -> input MUSI mieć placeholder.
     W Field bez explicit placeholder wstrzykujemy " " (pusty space). */
  const effectivePlaceholder =
    rest.placeholder ?? (ctx?.inField ? " " : undefined);

  const isPassword = type === "password";
  const hasToggle = isPassword && showPasswordToggle && !rest.disabled;
  const effectiveType = hasToggle && revealed ? "text" : type;

  /* Kolejność: spread `rest` FIRST -> potem jawne props z context. Jawne
     wygrywają (rest.placeholder uwzględniony już w effectivePlaceholder). */
  const inputElement = (
    <input
      ref={ref}
      {...rest}
      type={effectiveType}
      id={rest.id ?? ctx?.id}
      aria-describedby={rest["aria-describedby"] ?? ctx?.describedBy}
      aria-invalid={rest["aria-invalid"] ?? (effectiveInvalid || undefined)}
      aria-required={rest["aria-required"] ?? (ctx?.required || undefined)}
      placeholder={effectivePlaceholder}
      className={[styles.control, stateClass, className]
        .filter(Boolean)
        .join(" ")}
    />
  );

  /* Wrapper z toggle button-em — wewnątrz nadal może być przekazany w prefix/suffix. */
  const controlNode = hasToggle ? (
    <div className={styles.passwordWrap}>
      {inputElement}
      <button
        type="button"
        className={styles.passwordToggleBtn}
        onClick={() => setRevealed((v) => !v)}
        aria-label={revealed ? t.hide : t.show}
        aria-pressed={revealed}
      >
        <Icon name={revealed ? "eye-slash" : "eye"} />
      </button>
    </div>
  ) : (
    inputElement
  );

  /* Bez addons -> return controlNode bezpośrednio (raw input LUB passwordWrap). */
  if (prefix == null && suffix == null) {
    return controlNode;
  }

  return (
    <div className={styles.inputWithAddon}>
      {prefix != null && <span className={styles.addonPrefix}>{prefix}</span>}
      {controlNode}
      {suffix != null && <span className={styles.addonSuffix}>{suffix}</span>}
    </div>
  );
});

/* Inline i18n — primitive trzyma tylko 2 stringi per locale.
   Pasuje do wzorca `useT(translations)` z `lib/i18n/context.tsx`.
   Jawny `Record<Locale, ...>` (bez `as const`) — inaczej każdy locale dostaje
   własny literal type i `useT<T>` nie potrafi zunifikować T między locale'ami. */
const passwordToggleI18n: Record<Locale, { show: string; hide: string }> = {
  pl: { show: "Pokaż hasło", hide: "Ukryj hasło" },
  en: { show: "Show password", hide: "Hide password" },
  de: { show: "Passwort anzeigen", hide: "Passwort ausblenden" },
};

/* `Omit "prefix"` — bo `HTMLAttributes.prefix?: string` (RDFa attribute) koliduje
   typowo z naszym `prefix?: ReactNode`. Bez Omit'a TypeScript robi
   `string & ReactNode = string` i custom override nie działa. */
export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "prefix"> & {
  /** Wymuś stan `invalid` (czerwone obramowanie). Default = z Field context. */
  invalid?: boolean;
  /** Stan `success` (zielone obramowanie). Ignorowany gdy `invalid` true. */
  success?: boolean;
  /** Element przed inputem (icon / text). NIE kompatybilny z floating label. */
  prefix?: ReactNode;
  /** Element po inpucie. NIE kompatybilny z floating label. */
  suffix?: ReactNode;
  /** Dla `type="password"` — pokaż przycisk 👁 toggle visibility. Default `true`. */
  showPasswordToggle?: boolean;
};
